import type { AnalyticsEvent, AnalyticsEvents, UtmTags } from '@zuruflex/types';
import type { PostHog } from 'posthog-js';

/**
 * Product analytics for the website, behind the visitor's say-so.
 *
 * PostHog, loaded only in the browser and only once the visitor has
 * answered the consent banner — the privacy policy promised that the
 * banner comes first, and this is where that promise is kept:
 *
 * - **No answer yet:** nothing is loaded and nothing is sent.
 * - **Accepted:** events and session replays, under the same anonymous id
 *   the feature flags use, so a rollout and its effect are one person.
 * - **Declined:** cookieless counting. Nothing is stored on the device and
 *   PostHog identifies the visit by a server-side hash that rotates daily,
 *   so pages and funnels still add up but nobody can be followed. Replays
 *   stay off. (The PostHog project must have cookieless mode switched on,
 *   or these events are dropped at ingestion.)
 *
 * Every call is a no-op without `VITE_POSTHOG_KEY`, so development and any
 * environment nobody has set up simply send nothing.
 */
const KEY = import.meta.env.VITE_POSTHOG_KEY as string | undefined;
/** Our own `/ingest` proxy in production, so ad blockers leave it alone. */
const HOST = (import.meta.env.VITE_POSTHOG_HOST as string | undefined) ?? 'https://eu.i.posthog.com';

export type Consent = 'granted' | 'denied';

const CONSENT_KEY = 'zuruflex.consent';

/** First-touch campaign tags, held for the visit so a later tap can carry them. */
const UTM_KEY = 'zuruflex.utm';
const UTM_PARAMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;

let client: PostHog | null = null;
let loading: Promise<PostHog | null> | null = null;
/**
 * Events from before PostHog loaded, replayed once it has.
 *
 * Also what holds a first visit's events while the banner is unanswered:
 * somebody landing on a car from an ad has viewed it before they have
 * said yes or no, and that view is the one worth having. Held in memory
 * only, never sent until there is an answer, and gone with the tab if
 * there never is one. Capped, so a visitor who ignores the banner for an
 * hour does not grow it without limit.
 */
const queue: Array<(posthog: PostHog) => void> = [];
const QUEUE_LIMIT = 100;

export function readConsent(): Consent | null {
  try {
    const stored = window.localStorage.getItem(CONSENT_KEY);
    return stored === 'granted' || stored === 'denied' ? stored : null;
  } catch {
    return null;
  }
}

export function analyticsEnabled(): boolean {
  return Boolean(KEY);
}

/**
 * Loads PostHog for an answer already given. Called on boot when there is
 * a stored answer, and by the banner when there is a new one.
 */
export function startAnalytics(consent: Consent, anonymousId: string): void {
  if (!KEY || typeof window === 'undefined') return;

  if (client || loading) {
    // Changing one's mind after the fact: the same instance switches modes.
    void (loading ?? Promise.resolve(client)).then((posthog) => posthog && applyConsent(posthog, consent));
    return;
  }

  loading = import('posthog-js')
    .then(({ default: posthog }) => {
      posthog.init(KEY, {
        api_host: HOST,
        ui_host: 'https://eu.posthog.com',
        // The flags' id, so "who saw the new checkout" and "who booked"
        // are the same person in both systems. Ignored while declined:
        // cookieless mode sends a placeholder and PostHog hashes the rest.
        bootstrap: { distinctID: anonymousId },
        cookieless_mode: 'on_reject',
        // React Router changes pages with pushState, not page loads.
        capture_pageview: 'history_change',
        capture_pageleave: true,
        person_profiles: 'identified_only',
        session_recording: {
          // Every field and every piece of text on the page: a replay is
          // for seeing where people go, never what they typed or read out
          // of their own booking.
          maskAllInputs: true,
          maskTextSelector: '[data-private], [data-private] *',
        },
        autocapture: true,
      });
      applyConsent(posthog, consent);
      client = posthog;
      for (const send of queue.splice(0)) send(posthog);
      return posthog;
    })
    .catch(() => null);
}

/** What the banner calls. Remembers the answer and acts on it. */
export function setConsent(consent: Consent, anonymousId: string): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, consent);
  } catch {
    // Not remembered: the banner asks again next visit, which is the
    // honest outcome for a browser that will not keep the answer.
  }
  startAnalytics(consent, anonymousId);
}

function applyConsent(posthog: PostHog, consent: Consent) {
  if (consent === 'granted') {
    posthog.opt_in_capturing();
    posthog.startSessionRecording();
  } else {
    posthog.stopSessionRecording();
    posthog.opt_out_capturing();
  }
}

/** Sends one catalogued event, or queues it until PostHog has loaded. */
export function track<E extends AnalyticsEvent>(event: E, properties: AnalyticsEvents[E]): void {
  if (!KEY || typeof window === 'undefined') return;
  const send = (posthog: PostHog) => posthog.capture(event, properties as Record<string, unknown>);
  if (client) send(client);
  else if (queue.length < QUEUE_LIMIT) queue.push(send);
}

/**
 * Ties the anonymous visit to the account on sign-in, so what somebody
 * browsed before signing up counts towards the booking they made after.
 * Only with consent: a declined visit has no history to join.
 */
export function identify(customerId: string): void {
  if (client && readConsent() === 'granted') client.identify(customerId);
}

/** On sign-out, so the next person on a shared laptop starts clean. */
export function resetIdentity(): void {
  if (client && readConsent() === 'granted') client.reset();
}

/**
 * The campaign this visit arrived from. Read off the landing page's query
 * string once and kept for the tab, because the tap it is attributed to
 * usually happens three pages later, after the tags have gone from the URL.
 */
export function campaign(): UtmTags {
  if (typeof window === 'undefined') return {};
  try {
    const kept = window.sessionStorage.getItem(UTM_KEY);
    if (kept) return JSON.parse(kept) as UtmTags;
  } catch {
    // Fall through and read the URL.
  }

  const params = new URLSearchParams(window.location.search);
  const tags: UtmTags = {};
  for (const name of UTM_PARAMS) {
    const value = params.get(name);
    if (value) tags[name] = value.slice(0, 100);
  }
  try {
    window.sessionStorage.setItem(UTM_KEY, JSON.stringify(tags));
  } catch {
    // Not kept; the next read tries the URL again.
  }
  return tags;
}
